import React, { useState, useEffect } from 'react';
import { axios } from '../../context/AuthContext';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, Legend } from 'recharts';

const TL = (v) => new Intl.NumberFormat('tr-TR',{style:'currency',currency:'TRY',minimumFractionDigits:0}).format(v||0);
const AYLAR = ['Oca','Şub','Mar','Nis','May','Haz','Tem','Ağu','Eyl','Eki','Kas','Ara'];

// Mobil uyumluluk için global stil desteği
const mobileStyles = `
  @media (max-width: 768px) {
    .responsive-grid-3 { grid-template-columns: 1fr !important; }
    .responsive-grid-4 { grid-template-columns: 1fr 1fr !important; }
    .tab-bar { flex-wrap: wrap; }
    .tab-bar button { flex: 1; font-size: 0.8rem; padding: 8px 4px !important; white-space: nowrap; }
    
    /* Rapor Tabloları için Mobil Görünüm */
    .table-container table, .table-container thead, .table-container tbody, .table-container th, .table-container td, .table-container tr { 
      display: block; 
    }
    .table-container thead tr { position: absolute; top: -9999px; left: -9999px; }
    .table-container tr { border: 1px solid var(--border); margin: 8px; border-radius: 10px; background: var(--bg-card); padding: 8px; }
    .table-container td { border: none; position: relative; padding-left: 50% !important; text-align: right !important; min-height: 32px; display: flex; align-items: center; justify-content: flex-end; }
    .table-container td:before { 
      content: attr(data-label); position: absolute; left: 10px; width: 45%; padding-right: 10px; 
      white-space: nowrap; text-align: left; font-weight: 700; color: var(--text-secondary); font-size: 0.75rem;
    }
    
    .chart-container { height: 220px !important; }
  }
`;

export default function RaporlarPage() {
  const [aktifTab, setAktifTab]     = useState('gelir_gider');
  const [yilRapor, setYilRapor]     = useState(null);
  const [alacaklar, setAlacaklar]   = useState(null);
  const [tahminat, setTahminat]     = useState(null);
  const [yil, setYil]               = useState(new Date().getFullYear());
  const [loading, setLoading]       = useState(false);

  useEffect(() => {
    if (aktifTab === 'gelir_gider' && !yilRapor) loadGelirGider();
    if (aktifTab === 'alacaklar'   && !alacaklar) loadAlacaklar();
    if (aktifTab === 'tahminat'    && !tahminat) loadTahminat();
  // eslint-disable-next-line
  }, [aktifTab]);

  const loadGelirGider = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`/muhasebe/rapor/gelir-gider?yil=${yil}`);
      if (r.data.success) setYilRapor(r.data.data);
    } catch {} finally { setLoading(false); }
  };

  const loadAlacaklar = async () => {
    setLoading(true);
    try {
      const r = await axios.get('/muhasebe/rapor/alacaklar');
      if (r.data.success) setAlacaklar(r.data.data);
    } catch {} finally { setLoading(false); }
  };

  const loadTahminat = async () => {
    setLoading(true);
    try {
      const r = await axios.get('/muhasebe/rapor/tahminat');
      if (r.data.success) setTahminat(r.data.data);
    } catch {} finally { setLoading(false); }
  };

  const TABS = [
    { k:'gelir_gider', l:'📊 Gelir/Gider', },
    { k:'alacaklar',   l:'⚠️ Alacaklar',  },
    { k:'tahminat',    l:'🔮 Tahminler',   },
  ];

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:20, paddingBottom: 40 }}>
      <style>{mobileStyles}</style>
      <div className="animate-fadeInUp">
        <h2 style={{ fontFamily:'var(--font-ui)', fontWeight:900, fontSize:'1.4rem' }}>📊 Raporlar</h2>
        <p style={{ color:'var(--text-muted)', fontSize:'0.85rem', marginTop:4 }}>Finansal analiz ve raporlama</p>
      </div>

      {/* Tab bar */}
      <div className="tab-bar" style={{ display:'flex', gap:8 }}>
        {TABS.map(t => (
          <button key={t.k} className="btn"
            style={{
              background: aktifTab===t.k ? 'var(--blue)' : 'var(--bg-elevated)',
              color:      aktifTab===t.k ? 'white'      : 'var(--text-secondary)',
              border:     `1.5px solid ${aktifTab===t.k ? 'var(--blue)' : 'var(--border)'}`,
              fontWeight: 700,
            }}
            onClick={() => setAktifTab(t.k)}>
            {t.l}
          </button>
        ))}
      </div>

      {loading && <div className="page-loader" style={{ minHeight:200 }}><div className="spinner" /></div>}

      {/* Gelir/Gider Raporu */}
      {aktifTab === 'gelir_gider' && yilRapor && !loading && (
        <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
          {/* Yıl seçici */}
          <div style={{ display:'flex', gap:12, alignItems:'center' }}>
            <span style={{ fontWeight:700 }}>Yıl:</span>
            <select className="form-input" style={{ width:120 }} value={yil}
              onChange={e => { setYil(e.target.value); setYilRapor(null); setTimeout(loadGelirGider, 100); }}>
              {[2023,2024,2025,2026].map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>

          {/* Yıllık özet */}
          <div className="responsive-grid-3" style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:14 }}>
            {[
              { l:'Yıllık Gelir',  v:TL(parseFloat(yilRapor.ozet?.yillik_gelir)||0),                                      c:'#10B981', icon:'📈', bg:'#D1FAE5' },
              { l:'Yıllık Gider',  v:TL((parseFloat(yilRapor.ozet?.yillik_gider)||0)+(parseFloat(yilRapor.ozet?.yillik_maas)||0)), c:'#EF4444', icon:'📉', bg:'#FEE2E2' },
              { l:'Yıllık Kâr',    v:TL((parseFloat(yilRapor.ozet?.yillik_gelir)||0)-(parseFloat(yilRapor.ozet?.yillik_gider)||0)-(parseFloat(yilRapor.ozet?.yillik_maas)||0)), c:'#3B82F6', icon:'⚖️', bg:'#DBEAFE' },
            ].map((s,i) => (
              <div key={i} className="card" style={{ background:s.bg, border:`1.5px solid ${s.c}30`, padding:18 }}>
                <div style={{ fontSize:'1.8rem', marginBottom:8 }}>{s.icon}</div>
                <div style={{ fontFamily:'var(--font-ui)', fontWeight:900, fontSize:'1.25rem', color:s.c }}>{s.v}</div>
                <div style={{ fontSize:'0.78rem', color:s.c, fontWeight:600 }}>{s.l}</div>
              </div>
            ))}
          </div>

          {/* Aylık bar chart */}
          <div className="card">
            <div className="section-title" style={{ marginBottom:16, fontSize: '0.9rem' }}>Aylık Gelir / Gider / Kâr ({yilRapor.yil})</div>
            <div className="chart-container">
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={yilRapor.aylar.map(a => ({ ...a, ay: AYLAR[a.ay-1] }))} barSize={14}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                  <XAxis dataKey="ay" tick={{ fontSize:10, fill:'var(--text-muted)' }} />
                  <YAxis tickFormatter={v => (v/1000).toFixed(0)+'K'} tick={{ fontSize:10, fill:'var(--text-muted)' }} />
                  <Tooltip formatter={(v,n) => [TL(v), n==='gelir'?'Gelir':n==='gider'?'Gider':'Kâr']} />
                  <Legend iconSize={10} wrapperStyle={{fontSize: 12}} />
                  <Bar dataKey="gelir" fill="#10B981" name="Gelir" radius={[3,3,0,0]} />
                  <Bar dataKey="gider" fill="#EF4444" name="Gider" radius={[3,3,0,0]} />
                  <Bar dataKey="kar"   fill="#3B82F6" name="Kâr"   radius={[3,3,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      {/* Alacaklar Raporu */}
      {aktifTab === 'alacaklar' && alacaklar && !loading && (
        <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
          {/* Özet */}
          <div className="responsive-grid-4" style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12 }}>
            {[
              { l:'Gecikmiş',  v:TL(alacaklar.ozet?.gecikmiş_tutar),  c:'#EF4444', bg:'#FEE2E2', icon:'🔴' },
              { l:'Bekleyen',  v:TL(alacaklar.ozet?.bekleyen_tutar),  c:'#F59E0B', bg:'#FEF3C7', icon:'🟡' },
              { l:'Tahsil',    v:TL(alacaklar.ozet?.odenen_tutar),    c:'#10B981', bg:'#D1FAE5', icon:'🟢' },
              { l:'Adet',      v:`${alacaklar.ozet?.gecikmiş_sayi || 0}`, c:'#EF4444', bg:'#FEE2E2', icon:'⚠️' },
            ].map((s,i) => (
              <div key={i} className="card" style={{ background:s.bg, border:`1.5px solid ${s.c}30`, padding:12 }}>
                <div style={{ fontSize:'1.2rem', marginBottom:4 }}>{s.icon}</div>
                <div style={{ fontFamily:'var(--font-ui)', fontWeight:900, color:s.c, fontSize: '0.9rem' }}>{s.v}</div>
                <div style={{ fontSize:'0.65rem', color:s.c, fontWeight:600 }}>{s.l}</div>
              </div>
            ))}
          </div>

          {/* Gecikmiş taksitler */}
          <div className="card" style={{ padding:0 }}>
            <div style={{ padding:'14px 16px', borderBottom:'1px solid var(--border)', background:'#FEE2E2' }}>
              <div style={{ fontWeight:800, color:'#991B1B' }}>🔴 Vadesi Geçmiş Taksitler</div>
            </div>
            {!alacaklar.vadesi_gecmis?.length ? (
              <div className="empty-state" style={{ padding:32 }}><p>✅ Vadesi geçmiş taksit yok</p></div>
            ) : (
              <div className="table-container">
                <table className="table">
                  <thead><tr><th>Öğrenci</th><th>Veli Tel</th><th>Vade</th><th>Gecikme</th><th style={{textAlign:'right'}}>Tutar</th><th>Paket</th></tr></thead>
                  <tbody>
                    {alacaklar.vadesi_gecmis.map((g,i) => (
                      <tr key={i}>
                        <td data-label="Öğrenci" style={{ fontWeight:700 }}>{g.ad} {g.soyad}</td>
                        <td data-label="Veli Tel" style={{ fontSize:'0.82rem' }}>{g.veli_tel || g.telefon || '—'}</td>
                        <td data-label="Vade" style={{ fontSize:'0.82rem', color:'#EF4444' }}>{new Date(g.vade_tarihi).toLocaleDateString('tr-TR')}</td>
                        <td data-label="Gecikme">
                          <span className="badge" style={{ background:'#FEE2E2', color:'#EF4444' }}>
                            {g.gecikme_gun} gün
                          </span>
                        </td>
                        <td data-label="Tutar" style={{ textAlign:'right', fontFamily:'var(--font-ui)', fontWeight:900, color:'#EF4444' }}>{TL(g.tutar)}</td>
                        <td data-label="Paket" style={{ fontSize:'0.78rem', color:'var(--text-muted)' }}>{g.paket}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* 30 gün içinde vadesini dolacaklar */}
          {alacaklar.bekleyen_30gun?.length > 0 && (
            <div className="card" style={{ padding:0 }}>
              <div style={{ padding:'14px 16px', borderBottom:'1px solid var(--border)', background:'#FEF3C7' }}>
                <div style={{ fontWeight:800, color:'#92400E' }}>🟡 Önümüzdeki 30 Gün</div>
              </div>
              <div className="table-container">
                <table className="table">
                  <thead><tr><th>Öğrenci</th><th>Vade</th><th>Kalan Gün</th><th style={{textAlign:'right'}}>Tutar</th></tr></thead>
                  <tbody>
                    {alacaklar.bekleyen_30gun.map((t,i) => (
                      <tr key={i}>
                        <td data-label="Öğrenci" style={{ fontWeight:700 }}>{t.ad} {t.soyad}</td>
                        <td data-label="Vade" style={{ fontSize:'0.82rem' }}>{new Date(t.vade_tarihi).toLocaleDateString('tr-TR')}</td>
                        <td data-label="Kalan"><span className="badge badge-muted">{t.kalan_gun} gün</span></td>
                        <td data-label="Tutar" style={{ textAlign:'right', fontFamily:'var(--font-ui)', fontWeight:900, color:'#F59E0B' }}>{TL(t.tutar)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Tahminat Raporu */}
      {aktifTab === 'tahminat' && tahminat && !loading && (
        <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
          <div className="card">
            <div className="section-title" style={{ marginBottom:16, fontSize: '0.9rem' }}>🔮 12 Aylık Beklenen Nakit Girişi</div>
            <div className="chart-container">
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={tahminat}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                  <XAxis dataKey="ay_no" tickFormatter={v => AYLAR[parseInt(v.slice(5))-1]} tick={{ fontSize:10, fill:'var(--text-muted)' }} />
                  <YAxis tickFormatter={v => (v/1000).toFixed(0)+'K'} tick={{ fontSize:10, fill:'var(--text-muted)' }} />
                  <Tooltip formatter={(v,n) => [TL(v), 'Beklenen Gelir']} labelFormatter={v => AYLAR[parseInt(v.slice(5))-1]} />
                  <Line type="monotone" dataKey="beklenen" stroke="#10B981" strokeWidth={2} dot={{ fill:'#10B981', r:4 }} name="Beklenen" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="card" style={{ padding:0 }}>
            <div className="table-container">
              <table className="table">
                <thead><tr><th>Ay</th><th style={{textAlign:'center'}}>Taksit Sayısı</th><th style={{textAlign:'right'}}>Beklenen Gelir</th></tr></thead>
                <tbody>
                  {tahminat.map((r,i) => (
                    <tr key={i}>
                      <td data-label="Ay" style={{ fontWeight:700 }}>{r.ay}</td>
                      <td data-label="Taksit Sayısı" style={{ textAlign:'center', color:'var(--text-muted)' }}>{r.taksit_sayi}</td>
                      <td data-label="Beklenen" style={{ textAlign:'right', fontFamily:'var(--font-ui)', fontWeight:900, color:'#10B981', fontSize:'1rem' }}>{TL(r.beklenen)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}